// src/layouts/management/sidebar/hooks/useManagementSidebar.ts

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useDispatch,
  useSelector,
} from 'react-redux';

import type {
  AppDispatch,
  RootState,
} from '../../../../app/store';

import {
  clearTokens,
  setUser,
} from '../../../../features/auth/authSlice';

import {
  loginService,
} from '../../../../features/auth/login/services/LoginService';

import {
  memberService,
} from '../../../../features/member/services/memberService';

import {
  seedService,
} from '../../../../features/seed/services/seedService';

import type {
  StoreUserPoint,
} from '../../../../features/seed/types/seed.types';

import {
  storeService,
} from '../../../../features/store/services/storeService';

import type {
  StoreListItem,
} from '../../../../features/store/types/store.types';

import RouteConfig
  from '../../../../routes/RouteConfig';

import type {
  RouteItem,
} from '../../../../routes/RouteConfig';


type UseManagementSidebarParams = {
  currentPath: string;

  onNavigate: (
    route: RouteItem,
  ) => void;
};


export const useManagementSidebar = ({
  currentPath,
  onNavigate,
}: UseManagementSidebarParams) => {

  const dispatch =
    useDispatch<AppDispatch>();


  const user =
    useSelector(
      (
        state:
          RootState,
      ) =>
        state.auth.user,
    );


  const [
    storeList,
    setStoreList,
  ] =
    useState<
      StoreListItem[]
    >([]);


  const [
    storeBalances,
    setStoreBalances,
  ] =
    useState<
      StoreUserPoint[]
    >([]);


  const [
    isLoggingOut,
    setIsLoggingOut,
  ] =
    useState(false);


  /**
   * =========================================================
   * 권한
   * =========================================================
   */
  const isAdmin =
    user?.role ===
    'ADMIN';


  const isSuperAdmin =
    user?.role ===
    'SUPERADMIN';


  /**
   * =========================================================
   * 매장 ID -> 매장 이름
   * =========================================================
   */
  const storeNameMap =
    useMemo(
      () =>
        new Map<
          number,
          string
        >(
          storeList.map(
            (
              store,
            ) => [
              store.storeId,
              store.storeName,
            ],
          ),
        ),
      [
        storeList,
      ],
    );


  /**
   * =========================================================
   * 현재 메뉴 활성 여부
   * =========================================================
   */
  const isActive =
    useCallback(
      (
        path: string,
      ): boolean => {
        return (
          currentPath ===
          path
        );
      },
      [
        currentPath,
      ],
    );


  /**
   * =========================================================
   * 메뉴 Class
   * =========================================================
   */
  const getMenuClassName =
    useCallback(
      (
        path: string,
      ): string => {
        return [
          'flex w-full items-center gap-3',
          'rounded-lg px-4 py-3',
          'text-left',
          'transition',

          isActive(
            path,
          )
            ? 'bg-main-medium text-white'
            : 'text-gray-300 hover:bg-gray-800 hover:text-white',
        ].join(
          ' ',
        );
      },
      [
        isActive,
      ],
    );


  /**
   * =========================================================
   * Sidebar 잔액 전체 갱신
   *
   * 1. 로그인 사용자 정보
   *    -> 시드 잔액 user.point 갱신
   *
   * 2. 매장 목록
   *
   * 3. 로그인 사용자의 매장 포인트
   *
   * Sidebar에서는 다른 회원 조회용
   * 관리자 API를 사용하지 않습니다.
   * =========================================================
   */
  const loadStoreBalances =
    useCallback(
      async (): Promise<void> => {

        if (
          !user?.userId
        ) {

          setStoreList(
            [],
          );


          setStoreBalances(
            [],
          );


          return;
        }


        try {

          const [
            latestUser,
            stores,
            points,
          ] =
            await Promise.all([

              /**
               * 시드 잔액 포함
               * 최신 로그인 사용자 정보
               */
              memberService
                .getMyInfo(),


              /**
               * 전체 매장
               */
              storeService
                .getStores(),


              /**
               * 로그인 사용자의
               * 매장 포인트
               */
              seedService
                .getMyStorePointList(),

            ]);


          /**
           * ===============================================
           * 시드 잔액 갱신
           *
           * Sidebar에서 사용하는:
           *
           * user.point
           *
           * 가 Redux를 통해 변경되므로
           * Sidebar가 자동 리렌더링됩니다.
           * ===============================================
           */
          if (
            latestUser
          ) {

            dispatch(
              setUser(
                latestUser,
              ),
            );

          }


          const safeStores =
            Array.isArray(
              stores,
            )
              ? stores
              : [];


          const safePoints =
            Array.isArray(
              points,
            )
              ? points
              : [];


          /**
           * 매장 목록
           */
          setStoreList(
            safeStores,
          );


          /**
           * 매장 포인트
           */
          setStoreBalances(
            safePoints.filter(
              (
                point,
              ) =>
                point.storeId !==
                0,
            ),
          );

        } catch (
          error: unknown
        ) {

          console.error(
            'Sidebar 잔액 조회 실패:',
            error,
          );


          /**
           * 기존 user 정보까지
           * 제거하면 Sidebar 프로필까지
           * 사라질 수 있으므로
           *
           * 매장 관련 데이터만 초기화
           */
          setStoreList(
            [],
          );


          setStoreBalances(
            [],
          );

        }
      },
      [
        dispatch,
        user?.userId,
      ],
    );


  /**
   * =========================================================
   * 로그인 사용자 변경 시
   * 잔액 재조회
   * =========================================================
   */
  useEffect(
    () => {

      void loadStoreBalances();

    },
    [
      loadStoreBalances,
    ],
  );


  /**
   * =========================================================
   * balance-refresh 이벤트 감지
   *
   * 다른 페이지에서:
   *
   * window.dispatchEvent(
   *   new Event(
   *     'balance-refresh',
   *   ),
   * );
   *
   * 호출하면 Sidebar 잔액을
   * 서버에서 다시 조회합니다.
   * =========================================================
   */
  useEffect(
    () => {

      const handleBalanceRefresh =
        (): void => {

          void loadStoreBalances();

        };


      window.addEventListener(
        'balance-refresh',
        handleBalanceRefresh,
      );


      return () => {

        window.removeEventListener(
          'balance-refresh',
          handleBalanceRefresh,
        );

      };

    },
    [
      loadStoreBalances,
    ],
  );


  /**
   * =========================================================
   * 로그아웃
   * =========================================================
   */
  const logout =
    useCallback(
      async (): Promise<void> => {

        if (
          isLoggingOut
        ) {
          return;
        }


        try {

          setIsLoggingOut(
            true,
          );


          await loginService
            .logout();

        } catch (
          error: unknown
        ) {

          console.error(
            '로그아웃 요청 실패:',
            error,
          );

        } finally {

          dispatch(
            clearTokens(),
          );


          onNavigate(
            RouteConfig
              .Auth
              .Login,
          );


          setIsLoggingOut(
            false,
          );

        }
      },
      [
        dispatch,
        isLoggingOut,
        onNavigate,
      ],
    );


  return {

    user,

    storeBalances,

    storeNameMap,

    isAdmin,

    isSuperAdmin,

    isLoggingOut,

    getMenuClassName,

    loadStoreBalances,

    logout,

  };
};